'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/stores/authStore';
import { isProfileComplete, getMissingProfileFields } from '@/lib/profileCompletion';
import { Calendar, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslations } from 'next-intl';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1/api';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the profile is complete (before onClose). The modal never navigates: the customer stays on the page. */
  onCompleted?: () => void;
  /** Show a close button and let Escape close it (the booking wizard). The global guard keeps it forced. */
  dismissible?: boolean;
  /** Stacking override, e.g. 'z-[200]' above the booking wizard. */
  zClassName?: string;
}

export default function ProfileCompletionModal({ isOpen, onClose, onCompleted, dismissible = false, zClassName = 'z-50' }: ProfileCompletionModalProps) {
  const { user, token, updateUser } = useAuthStore();
  const t = useTranslations('profileCompletion');
  const tCommon = useTranslations('common');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    birthday: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        birthday: user.birthday || '',
      });
    }
  }, [user]);

  // Prevent closing modal by clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (dismissible) onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // Only prevent if clicking on the backdrop, not on form elements
      const target = e.target as Element;
      if (target && target.closest('.modal-content')) {
        return; // Allow clicks inside the modal content
      }
      e.preventDefault();
      e.stopPropagation();
    };

    if (!isOpen) return;
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('click', handleClickOutside);
    // Prevent body scroll; give back what was there (the booking wizard keeps its own lock).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, dismissible, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Enhanced validation with visual feedback
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.first_name || formData.first_name.trim() === '') {
      newErrors.first_name = t('firstNameRequired');
    }
    
    if (!formData.last_name || formData.last_name.trim() === '') {
      newErrors.last_name = t('lastNameRequired');
    }
    
    if (!formData.birthday || formData.birthday.trim() === '') {
      newErrors.birthday = t('birthdayRequired');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(t('fillAllRequiredFields'));
      return;
    }
    
    // Clear any existing errors
    setErrors({});

    if (!token) {
      toast.error(t('authError'));
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        birthday: formData.birthday,
      };

      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      if (data.success) {
        updateUser(data.data);
        
        // Check if profile is now complete after update
        const { isProfileComplete } = await import('@/lib/profileCompletion');
        const profileComplete = isProfileComplete(data.data);
        
        // Also check backend response for profile completion status
        const backendProfileComplete = data.data.isProfileComplete !== false;

        if (profileComplete && backendProfileComplete) {
          toast.success(t('profileCompletedSuccessfully'));
          // Stay on the page (the payment page, the booking wizard…): no redirect.
          onCompleted?.();
          onClose();
        } else {
          toast.error(t('completeAllRequiredData'));
        }
      } else {
        toast.error(data.message || t('updateDataError'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('connectionError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || typeof document === 'undefined') return null;

  // On the body, so a parent's transform or backdrop filter (the booking wizard) cannot trap or clip it.
  return createPortal(
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zClassName} p-4`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.preventDefault()}
    >
      <div 
        className="modal-content bg-white rounded-[22px] shadow-[inset_0_0_0_1px_rgba(57,22,77,.1)]-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 text-start">
          {dismissible && (
            <button type="button" onClick={onClose} aria-label={tCommon('close')}
              className="absolute end-4 top-4 grid h-9 w-9 place-items-center rounded-full text-deep/60 hover:bg-deep/5">
              <X className="h-5 w-5" />
            </button>
          )}
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="flex items-center">
              <div className="bg-primary-100 p-2 rounded-lg mx-3">
                <AlertTriangle className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-deep">{t('completeProfile')}</h2>
                <p className="text-sm text-deep/70">{t('completeRequiredData')}</p>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">{t('importantNotice')}</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  {t('profileCompletionWarningWithLastName')}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-deep/80 mb-2">
                {t('firstName')} *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.first_name ? 'border-red-500' : 'border-deep/20'
                }`}
                placeholder={t('enterFirstName')}
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-deep/80 mb-2">
                {t('lastName')} *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.last_name ? 'border-red-500' : 'border-deep/20'
                }`}
                placeholder={t('enterLastNameRequired')}
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            <div>
              <label htmlFor="birthday" className="block text-sm font-medium text-deep/80 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                {t('birthday')} *
              </label>
              <input
                type="date"
                id="birthday"
                name="birthday"
                value={formData.birthday}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.birthday ? 'border-red-500' : 'border-deep/20'
                }`}
              />
              {errors.birthday && (
                <p className="mt-1 text-sm text-red-600">{errors.birthday}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="button"
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(e as any);
                }}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? tCommon('saving') : t('completeProfile')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}
